// PDF → EPUB conversion for PageVoice.
//
// Pipeline:
//   1. Load the PDF with pdfjs-dist (browser, no server round-trip).
//   2. Extract metadata (title, author, page count) from the PDF dictionary.
//   3. Walk every page, pull text content, and join items into paragraphs
//      using the y-coordinate gaps as paragraph breaks.
//   4. Detect chapter boundaries — "CHAPTER N", "Chapter I", "PART N",
//      Roman/numeric headings on a near-empty page — and fall back to a
//      fixed page-window if the PDF has no chapter structure.
//   5. Assemble an EPUB 3 zip (jszip): mimetype (uncompressed) + META-INF +
//      OEBPS with content.opf, nav.xhtml, toc.ncx, styles.css, and one
//      XHTML file per chapter.
//   6. Return a File the rest of the app can treat like a normal EPUB —
//      the same importEpub() pipeline takes over from there.
//
// Originals are never modified; everything happens on a Blob in memory.

// Pinned to pdfjs-dist 3.11.174 LEGACY build deliberately. The legacy
// bundle spawns its worker as a CLASSIC worker (no `type: "module"`), which
// dodges the iOS Safari "undefined is not a function (near '...i of e...')"
// error we hit with pdfjs 6's module workers. Conversion is the same speed
// either way for the sizes we care about.
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.js?url";
import JSZip from "jszip";

// Vite-bundled worker URL. Picked up at load time so multiple imports share
// the same worker and we never miss the wiring during route changes.
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;


export interface PdfImportProgress {
  /** "loading" | "extracting" | "assembling" | "done" */
  phase: "loading" | "extracting" | "assembling" | "done";
  /** Current page being processed (1-based). */
  page?: number;
  /** Total pages, if known. */
  pages?: number;
  /** Free-form status string for the UI. */
  message?: string;
}

export interface PdfImportOptions {
  /** Override the title detected from PDF metadata. */
  title?: string;
  /** Override the author detected from PDF metadata. */
  author?: string;
  /** Approximate target chapter length when the PDF has no chapter markers. */
  pagesPerChapter?: number;
  /** Called with progress updates so the UI can show "page 14/300" etc. */
  onProgress?: (p: PdfImportProgress) => void;
}


/** Public entry point: convert a PDF File into an EPUB File. */
export async function convertPdfToEpub(
  pdfFile: File,
  opts: PdfImportOptions = {},
): Promise<File> {
  const progress = opts.onProgress ?? (() => {});
  const pagesPerChapter = Math.max(4, opts.pagesPerChapter ?? 12);

  progress({ phase: "loading", message: "Reading PDF…" });

  let buf: ArrayBuffer;
  try {
    buf = await pdfFile.arrayBuffer();
  } catch (e) {
    throw new Error(`Couldn't read the PDF file: ${stringifyErr(e)}`);
  }

  let pdf;
  try {
    pdf = await pdfjsLib.getDocument({ data: new Uint8Array(buf) }).promise;
  } catch (e) {
    throw new Error(`PDF.js couldn't open the file (it may be password-protected or corrupted): ${stringifyErr(e)}`);
  }

  // ---- Metadata ----------------------------------------------------------
  const docInfo = await pdf.getMetadata().catch(() => null);
  const pdfTitle = pickString(docInfo?.info, "Title");
  const pdfAuthor = pickString(docInfo?.info, "Author");
  const pdfLang   = pickString(docInfo?.info, "Language") || "en";
  const title  = (opts.title  ?? pdfTitle  ?? pdfFile.name.replace(/\.pdf$/i, "")).trim() || "Untitled";
  const author = (opts.author ?? pdfAuthor ?? "").trim() || "Unknown";

  // ---- Cover image ------------------------------------------------------
  // Render page 1 to a canvas, encode JPEG, store as cover.jpg in OEBPS so
  // the EPUB carries a proper cover-image and BookCard has something to show
  // beyond just the title text. Best-effort: failures don't fail the import.
  let coverJpeg: Uint8Array | null = null;
  try {
    coverJpeg = await renderCoverJpeg(pdf);
  } catch (e) {
    console.warn("[pagevoice/pdfImport] cover render failed:", e);
  }

  // ---- Per-page text -----------------------------------------------------
  progress({ phase: "extracting", page: 0, pages: pdf.numPages });
  const pages: string[] = [];
  let firstPageError: unknown = null;
  let failedPages = 0;
  for (let i = 1; i <= pdf.numPages; i++) {
    try {
      const page = await pdf.getPage(i);
      const txt  = await page.getTextContent({ includeMarkedContent: false });
      // Defensive: make sure items is iterable. pdfjs returns Array, but
      // be safe against future API drift and weird PDFs that emit no items.
      const itemsRaw = txt && (txt.items as unknown);
      const items: TextItemLike[] = Array.isArray(itemsRaw)
        ? (itemsRaw as TextItemLike[]).filter(isTextItemLike)
        : [];
      pages.push(itemsToParagraphs(items));
    } catch (e) {
      // One bad page shouldn't kill the whole import — skip it with an
      // inline placeholder so chapter alignment isn't destroyed. But if a
      // catastrophic failure mode kills every page (worker dead, MIME bad,
      // file unreadable), throw with the first real error after the loop
      // so the user sees what actually happened.
      failedPages++;
      if (firstPageError === null) firstPageError = e;
      // eslint-disable-next-line no-console
      console.error(`[pagevoice/pdfImport] page ${i} failed:`, e);
      pages.push(`[Page ${i} could not be extracted: ${stringifyErr(e).slice(0, 200)}]`);
    }
    progress({ phase: "extracting", page: i, pages: pdf.numPages });
  }

  // If basically nothing worked, surface the underlying cause rather than
  // shipping a useless EPUB full of placeholders.
  if (pdf.numPages >= 3 && failedPages >= pdf.numPages - 1) {
    throw new Error(
      `PDF text extraction failed on every page. First error: ${stringifyErr(firstPageError)}. ` +
      `This usually means the pdf.js worker couldn't start — check that ` +
      `your browser has fresh files (hard-refresh) and the .mjs MIME type ` +
      `is application/javascript.`
    );
  }

  // ---- Group pages into chapters ----------------------------------------
  progress({ phase: "assembling", message: "Building chapters…" });
  const chapters = groupIntoChapters(pages, pagesPerChapter);

  // ---- Assemble EPUB -----------------------------------------------------
  const zip = new JSZip();

  // mimetype MUST be the first entry and uncompressed for EPUB to validate.
  zip.file("mimetype", "application/epub+zip", { compression: "STORE" });

  zip.file("META-INF/container.xml", CONTAINER_XML);

  // Build manifest + spine items from chapters.
  const manifestItems: string[] = [];
  const spineItems: string[] = [];
  const navListItems: string[] = [];
  const ncxNavPoints: string[] = [];

  chapters.forEach((ch, idx) => {
    const id   = `chap${String(idx + 1).padStart(3, "0")}`;
    const href = `chapters/${id}.xhtml`;
    manifestItems.push(
      `<item id="${id}" href="${href}" media-type="application/xhtml+xml"/>`
    );
    spineItems.push(`<itemref idref="${id}"/>`);
    navListItems.push(
      `<li><a href="${href}">${xmlEscape(ch.title)}</a></li>`
    );
    ncxNavPoints.push(
      `<navPoint id="np-${idx + 1}" playOrder="${idx + 1}">` +
        `<navLabel><text>${xmlEscape(ch.title)}</text></navLabel>` +
        `<content src="${href}"/>` +
      `</navPoint>`
    );
    zip.file(`OEBPS/${href}`, chapterXhtml(ch.title, ch.paragraphs));
  });

  zip.file("OEBPS/styles.css", STYLES_CSS);
  zip.file("OEBPS/nav.xhtml",  navXhtml(title, navListItems.join("")));
  zip.file("OEBPS/toc.ncx",    tocNcx(title, ncxNavPoints.join("")));

  // Inject the cover file + manifest entry if we successfully rendered one.
  let coverManifestExtra = "";
  let coverMetaExtra = "";
  if (coverJpeg) {
    zip.file("OEBPS/cover.jpg", coverJpeg);
    coverManifestExtra =
      `  <item id="cover-image" href="cover.jpg" media-type="image/jpeg" properties="cover-image"/>\n` +
      `  <item id="cover-xhtml" href="cover.xhtml" media-type="application/xhtml+xml"/>`;
    coverMetaExtra = `  <meta name="cover" content="cover-image"/>`;
    zip.file("OEBPS/cover.xhtml", coverXhtml(title));
  }

  zip.file("OEBPS/content.opf", contentOpf({
    title, author, lang: pdfLang,
    manifestItems: [coverManifestExtra, manifestItems.join("\n    ")]
      .filter(Boolean)
      .join("\n    "),
    spineItems: [
      coverJpeg ? `<itemref idref="cover-xhtml" linear="yes"/>` : "",
      spineItems.join("\n    "),
    ].filter(Boolean).join("\n    "),
    metaExtra: coverMetaExtra,
    // Stable identifier — derived from file name + size, but if we re-import
    // the same PDF this will still match the EPUB the user previously had.
    bookId: stableBookId(title, author, pdfFile.size),
  }));

  const epubBlob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/epub+zip",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });

  const safeName = sanitizeFilename(title) + ".epub";
  const epubFile = new File([epubBlob], safeName, {
    type: "application/epub+zip",
  });

  progress({ phase: "done", message: `Built ${chapters.length} chapters from ${pdf.numPages} pages.` });
  return epubFile;
}


// ============================================================================
// Helpers
// ============================================================================

type TextItemLike = {
  str: string;
  transform?: number[];      // [scaleX, skewY, skewX, scaleY, x, y]
  height?: number;
  hasEOL?: boolean;
};

/** Convert pdfjs text items into a single string with paragraph breaks. */
function itemsToParagraphs(items: TextItemLike[]): string {
  if (!items?.length) return "";

  // We sort top-to-bottom, then left-to-right, by averaging item baselines.
  // pdfjs returns items roughly in reading order already, but some PDFs
  // emit columns out of order — this normalization is cheap insurance.
  const lines: { y: number; text: string; height: number }[] = [];
  let curY: number | null = null;
  let curText = "";
  let curHeight = 0;

  for (const it of items) {
    const y = it.transform ? it.transform[5] : 0;
    const h = it.height || 12;
    if (curY === null || Math.abs(y - curY) < (h * 0.4)) {
      // Same visual line — keep accumulating.
      curText += (curText && !/\s$/.test(curText) ? " " : "") + (it.str ?? "");
      curY = curY === null ? y : (curY + y) / 2;
      curHeight = Math.max(curHeight, h);
    } else {
      if (curText.trim()) lines.push({ y: curY ?? 0, text: curText.trim(), height: curHeight });
      curText = it.str ?? "";
      curY = y;
      curHeight = h;
    }
  }
  if (curText.trim()) lines.push({ y: curY ?? 0, text: curText.trim(), height: curHeight });

  // pdfjs Y goes UP — sort high-Y first so we read top-to-bottom.
  lines.sort((a, b) => b.y - a.y);

  // Stitch lines into paragraphs. A blank gap (line-height * 1.6) or a final
  // sentence-terminator on a short line is a paragraph break.
  const paragraphs: string[] = [];
  let para = "";
  let lastY: number | null = null;
  let lastHeight = 0;

  for (const ln of lines) {
    const gap = lastY != null ? (lastY - ln.y) : 0;
    const bigGap = lastHeight > 0 && gap > lastHeight * 1.6;
    const endsSentence = /[.!?…)"”]\s*$/.test(para);
    if (bigGap && endsSentence) {
      if (para.trim()) paragraphs.push(para.trim());
      para = ln.text;
    } else {
      // Join with a hyphenation-safe space.
      if (para && para.endsWith("-")) {
        para = para.slice(0, -1) + ln.text;
      } else if (para) {
        para += " " + ln.text;
      } else {
        para = ln.text;
      }
    }
    lastY = ln.y;
    lastHeight = ln.height;
  }
  if (para.trim()) paragraphs.push(para.trim());

  return paragraphs.join("\n\n");
}


interface ChapterDraft {
  title: string;
  paragraphs: string[];
}


/** Cluster page-level text into chapters using simple heuristics, then
 *  fall back to a fixed page-count window if no chapter markers exist. */
function groupIntoChapters(
  pages: string[],
  pagesPerChapter: number,
): ChapterDraft[] {
  // Look for chapter-like first lines per page.
  const breaks: { page: number; title: string }[] = [];
  const chapterLineRe =
    /^\s*(chapter|chap\.?|part|book)\s+([ivxlcdm]+|\d+)\b(.*)$/i;
  const numericTitleRe = /^\s*(\d{1,3})[.)\s]+([A-Z].{2,80})$/;
  const allCapsTitleRe = /^\s*([A-Z][A-Z\s'&\-]{4,60})$/;

  pages.forEach((pgText, i) => {
    const firstNonEmpty = (pgText.split(/\n+/).find((s) => s.trim()) ?? "").trim();
    if (!firstNonEmpty) return;
    const head = firstNonEmpty.slice(0, 120);

    let m = head.match(chapterLineRe);
    if (m) {
      const num = m[2];
      const tail = (m[3] || "").trim().replace(/^[—:.\-\s]+/, "");
      breaks.push({
        page: i,
        title: `${capitalize(m[1])} ${num}${tail ? " — " + tail : ""}`,
      });
      return;
    }
    m = head.match(numericTitleRe);
    if (m && head.length < 80) {
      breaks.push({ page: i, title: `${m[1]}. ${m[2].trim()}` });
      return;
    }
    m = head.match(allCapsTitleRe);
    if (m && head.length < 60) {
      breaks.push({ page: i, title: titleCase(m[1].trim()) });
      return;
    }
  });

  // Need at least 2 detected breaks to trust the heuristic; otherwise
  // collapse into evenly-sized windows.
  if (breaks.length < 2) {
    return windowedChapters(pages, pagesPerChapter);
  }

  // Build chapters from breakpoints.
  const out: ChapterDraft[] = [];
  for (let i = 0; i < breaks.length; i++) {
    const start = breaks[i].page;
    const end = i + 1 < breaks.length ? breaks[i + 1].page : pages.length;
    const text = pages.slice(start, end).join("\n\n");
    out.push({ title: breaks[i].title, paragraphs: text.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean) });
  }

  // If the very first detected chapter starts past page 0, prepend a
  // front-matter chapter so we don't lose pages.
  if (breaks[0].page > 0) {
    const intro = pages.slice(0, breaks[0].page).join("\n\n");
    out.unshift({
      title: "Front matter",
      paragraphs: intro.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean),
    });
  }

  return out.filter((c) => c.paragraphs.length > 0);
}


function windowedChapters(pages: string[], pagesPerChapter: number): ChapterDraft[] {
  const out: ChapterDraft[] = [];
  const total = pages.length;
  if (total === 0) return out;
  let chapter = 0;
  for (let start = 0; start < total; start += pagesPerChapter) {
    chapter++;
    const end = Math.min(start + pagesPerChapter, total);
    const text = pages.slice(start, end).join("\n\n");
    const paragraphs = text.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);
    out.push({
      title: `Chapter ${chapter} — pages ${start + 1}–${end}`,
      paragraphs,
    });
  }
  return out;
}


// ---- EPUB file templates ---------------------------------------------------


function chapterXhtml(title: string, paragraphs: string[]): string {
  const body = paragraphs
    .map((p) => `<p>${xmlEscape(p)}</p>`)
    .join("\n  ");
  return `<?xml version="1.0" encoding="utf-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="en">
<head>
<title>${xmlEscape(title)}</title>
<meta charset="utf-8"/>
<link rel="stylesheet" type="text/css" href="../styles.css"/>
</head>
<body>
<section epub:type="chapter">
  <h1>${xmlEscape(title)}</h1>
  ${body}
</section>
</body>
</html>
`;
}

function navXhtml(title: string, listItems: string): string {
  return `<?xml version="1.0" encoding="utf-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="en">
<head>
<title>${xmlEscape(title)} — Contents</title>
<meta charset="utf-8"/>
</head>
<body>
<nav epub:type="toc" id="toc">
  <h1>Contents</h1>
  <ol>${listItems}</ol>
</nav>
</body>
</html>
`;
}

function tocNcx(title: string, navPoints: string): string {
  return `<?xml version="1.0" encoding="utf-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
<head>
  <meta name="dtb:uid" content="${xmlEscape(title)}"/>
  <meta name="dtb:depth" content="1"/>
  <meta name="dtb:totalPageCount" content="0"/>
  <meta name="dtb:maxPageNumber" content="0"/>
</head>
<docTitle><text>${xmlEscape(title)}</text></docTitle>
<navMap>
  ${navPoints}
</navMap>
</ncx>
`;
}


function contentOpf(args: {
  title: string;
  author: string;
  lang: string;
  manifestItems: string;
  spineItems: string;
  bookId: string;
  /** Extra metadata block injected when we have a cover image — picked up
   *  by the legacy `<meta name="cover" content="..."/>` reader heuristic. */
  metaExtra?: string;
}): string {
  return `<?xml version="1.0" encoding="utf-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="BookId">
<metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
  <dc:identifier id="BookId">urn:pagevoice:${xmlEscape(args.bookId)}</dc:identifier>
  <dc:title>${xmlEscape(args.title)}</dc:title>
  <dc:creator>${xmlEscape(args.author)}</dc:creator>
  <dc:language>${xmlEscape(args.lang)}</dc:language>
  <meta property="dcterms:modified">${new Date().toISOString().replace(/\.\d{3}Z$/, "Z")}</meta>
  <meta name="generator" content="PageVoice PDF Importer"/>
${args.metaExtra ?? ""}
</metadata>
<manifest>
  <item id="nav"    href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>
  <item id="ncx"    href="toc.ncx"   media-type="application/x-dtbncx+xml"/>
  <item id="styles" href="styles.css" media-type="text/css"/>
  ${args.manifestItems}
</manifest>
<spine toc="ncx">
  ${args.spineItems}
</spine>
</package>
`;
}

function coverXhtml(title: string): string {
  // Full-page cover image, no extra chrome. Used as the first spine entry
  // so the EPUB opens onto the cover, like a real book.
  return `<?xml version="1.0" encoding="utf-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops" lang="en">
<head>
<title>${xmlEscape(title)}</title>
<meta charset="utf-8"/>
<style>
  body { margin: 0; padding: 0; background: #000; }
  .cover { display: block; width: 100%; height: 100vh; object-fit: contain; }
</style>
</head>
<body epub:type="cover">
  <img class="cover" src="cover.jpg" alt="${xmlEscape(title)} — cover"/>
</body>
</html>
`;
}


const CONTAINER_XML = `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>
`;

const STYLES_CSS = `/* PageVoice — converted-from-PDF EPUB defaults */
@charset "utf-8";
body { font-family: Georgia, "Iowan Old Style", serif; line-height: 1.6; }
h1   { font-family: "Playfair Display", Georgia, serif; font-weight: 700;
       font-size: 1.6em; margin: 1.4em 0 0.8em; line-height: 1.2; }
p    { margin: 0 0 0.9em; text-indent: 1.2em; }
p:first-of-type { text-indent: 0; }
`;


// ---- Tiny utilities --------------------------------------------------------


function xmlEscape(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s;
}

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w.length > 2 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function pickString(info: unknown, key: string): string | undefined {
  if (!info || typeof info !== "object") return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const v = (info as any)[key];
  if (typeof v === "string") {
    const trimmed = v.trim();
    return trimmed || undefined;
  }
  return undefined;
}

function sanitizeFilename(s: string): string {
  return s
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 120);
}

/** Render page 1 to a JPEG so the EPUB carries a real cover image. Sizes
 *  the canvas so the long side is ~900px — enough for a sharp BookCard
 *  thumbnail at common pixel densities without blowing up the file. */
async function renderCoverJpeg(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pdf: any,
  maxLongSide = 900,
  quality = 0.82,
): Promise<Uint8Array | null> {
  const page = await pdf.getPage(1);
  const baseViewport = page.getViewport({ scale: 1 });
  const longSide = Math.max(baseViewport.width, baseViewport.height);
  const scale = Math.min(2.5, maxLongSide / Math.max(longSide, 1));
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width  = Math.max(1, Math.ceil(viewport.width));
  canvas.height = Math.max(1, Math.ceil(viewport.height));
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Fill white first — PDF pages usually assume a white paper background,
  // and a transparent canvas would render the text as if on the EPUB's
  // (potentially dark) reader theme behind the cover image.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // pdf.js 3.x render API: pass canvasContext + viewport.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  await page.render({ canvasContext: ctx as any, viewport }).promise;

  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality),
  );
  if (!blob) return null;
  const buf = await blob.arrayBuffer();
  return new Uint8Array(buf);
}


function isTextItemLike(x: unknown): x is TextItemLike {
  if (!x || typeof x !== "object") return false;
  // TextMarkedContent has only {type, id}. TextItem has `str` (string).
  // We only want items that actually carry text.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof (x as any).str === "string";
}

function stringifyErr(e: unknown): string {
  if (!e) return "(unknown error)";
  if (e instanceof Error) return e.message || e.name || String(e);
  if (typeof e === "string") return e;
  try {
    return JSON.stringify(e).slice(0, 280);
  } catch {
    return String(e);
  }
}

function stableBookId(title: string, author: string, size: number): string {
  // Cheap deterministic ID — same PDF → same EPUB identifier, so re-imports
  // do not pile up duplicate entries with the same content.
  const raw = `${title.toLowerCase()}|${author.toLowerCase()}|${size}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) - hash + raw.charCodeAt(i)) | 0;
  }
  return `pdfimp-${Math.abs(hash).toString(36)}-${size.toString(36)}`;
}
